import { FormattedMessage, useIntl } from "react-intl";
import { Navigate, useNavigate, useParams } from "react-router";
import { useQuery } from "@apollo/client/react";
import {
  PublishedQuestionnaireGroupPartsDocument,
  PublishedQuestionnaireGroupsDocument,
} from "@/graphql/generated/operations";
import { BackButton } from "@/components/common/BackButton";
import { NoBackend } from "@/components/common/NoBackend";
import { TileArtwork } from "@/components/grow/TileArtwork";
import { groupImageSrc, localizedText, tileHueFromId } from "@/lib/questionnaire-group";

// Overview of one questionnaire group: its parts, which of them the user has
// already answered, and where to continue (genesis#183, spark#196). This is the
// generalisation of the old Power Quiz page - the group supplies the title, the
// description and the order, so the page carries no knowledge of any particular
// questionnaire, and no assumption about how many parts there are.
//
// genesis has no single-group query, so the group is found by id in the published
// list. A group that was set back to DRAFT drops out of that list, which is
// exactly the behaviour this page wants: the deep link lands in the empty state
// rather than in an error.

export function QuestionnaireGroupPage() {
  const intl = useIntl();
  const navigate = useNavigate();
  const { questionnaireGroupId = "" } = useParams();

  // cache-and-network so the answered flags refresh when returning from a part.
  const { data, loading } = useQuery(PublishedQuestionnaireGroupPartsDocument, {
    fetchPolicy: "cache-and-network",
  });
  const found = (data?.publishedQuestionnaireGroups ?? []).find(
    (g) => g.questionnaireGroupId === questionnaireGroupId,
  );
  // A group with no active questions is treated as absent here too, so a deep
  // link into an empty group ends in the same empty state as a withdrawn one
  // instead of opening a runner with nothing to answer.
  const group = found?.itemCount ? found : undefined;

  // The artwork comes from the catalogue selection, not from the parts query
  // above: that one is refetched after every submitted part and must stay free
  // of inlined image bytes. The catalogue query is cache-first, so arriving from
  // the Play page costs no request at all, and a deep link fetches it once.
  // QuestionnaireGroup is normalised by id, so both selections share one entity.
  const { data: catalogue } = useQuery(PublishedQuestionnaireGroupsDocument);
  const imageSrc =
    groupImageSrc(
      catalogue?.publishedQuestionnaireGroups.find(
        (g) => g.questionnaireGroupId === questionnaireGroupId,
      )?.image,
    ) ?? undefined;

  // Editorial copy, never translated in the client - only fallen back to the
  // other language when this one was left empty.
  const title = localizedText(group?.name);
  const description = localizedText(group?.description);

  const parts = group?.parts ?? [];
  const total = parts.length;
  const answeredCount = parts.reduce((n, p) => (p.answered ? n + 1 : n), 0);
  const next = parts.find((p) => !p.answered);

  // A single-part group has nothing to overview: one row and a button that both
  // lead to the same place. Redirect straight into the runner, replacing the
  // history entry so Back returns to the catalogue rather than bouncing here.
  const onlyPart = total === 1 ? parts[0] : undefined;
  if (onlyPart) {
    return (
      <Navigate
        to={`/grow/play/group/${questionnaireGroupId}/${onlyPart.questionnaire.questionnaireId}`}
        replace
      />
    );
  }

  return (
    <section
      className="mx-auto max-w-screen-md px-4 py-12"
      data-graphql="PublishedQuestionnaireGroupParts, PublishedQuestionnaireGroups"
    >
      <div className="mb-5">
        <BackButton />
      </div>
      <header className="mb-8 flex items-start justify-between gap-6">
        <div>
          <p className="text-xs tracking-wide text-ink/55">
            <FormattedMessage id="grow.play.quiz.eyebrow" defaultMessage="Grow · Play" />
          </p>
          {group ? <h1 className="mt-1 text-3xl font-semibold">{title}</h1> : null}
          {description ? <p className="mt-2 text-sm text-ink/55">{description}</p> : null}
        </div>
        {group ? (
          // Without artwork the group keeps its identity through the same
          // id-derived hue the catalogue tile falls back to.
          <TileArtwork
            imageSrc={imageSrc}
            hue={tileHueFromId(group.questionnaireGroupId)}
            variant="grid"
            className="hidden aspect-[6/5] w-56 shrink-0 rounded-xl ring-1 ring-black/10 sm:block"
          />
        ) : null}
      </header>

      {loading && !group ? (
        <p className="py-8 text-sm text-ink/55">
          <FormattedMessage id="common.loading" defaultMessage="Loading…" />
        </p>
      ) : total === 0 ? (
        <NoBackend
          label={intl.formatMessage({
            id: "grow.play.group.missing",
            defaultMessage: "This questionnaire is not available on this environment.",
          })}
        />
      ) : (
        <div className="space-y-6 pb-24">
          <div className="text-[11px] text-ink/55">
            <FormattedMessage
              id="grow.play.quiz.partsProgress"
              defaultMessage="{answered} of {total} parts answered"
              values={{ answered: answeredCount, total }}
            />
          </div>

          <div className="flex flex-col gap-2">
            {parts.map((p, i) => {
              const isNext = p.questionnaire.questionnaireId === next?.questionnaire.questionnaireId;
              return (
                <button
                  key={p.questionnaire.questionnaireId}
                  type="button"
                  onClick={() =>
                    navigate(
                      `/grow/play/group/${questionnaireGroupId}/${p.questionnaire.questionnaireId}`,
                    )
                  }
                  className={`flex items-center gap-3 rounded-lg px-4 py-3 text-left text-[14px] ring-1 transition focus:outline-none ${
                    p.answered
                      ? "bg-ink text-on-ink ring-ink"
                      : `bg-white text-ink/70 hover:text-ink hover:ring-accent ${
                          isNext ? "ring-2 ring-accent/60" : "ring-black/15"
                        }`
                  }`}
                >
                  <span
                    className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-[11px] font-medium ${
                      p.answered ? "bg-cream/20 text-on-ink" : "bg-ink/[0.06] text-ink/60"
                    }`}
                  >
                    {p.answered ? "✓" : i + 1}
                  </span>
                  {/* The editor's own name for the part. Questionnaire.name is a single
                      non-localized column, so it reads the same in both locales - the
                      numbered badge to its left carries the position. The positional
                      label is only the fallback for a part saved without a name. */}
                  <span className="min-w-0 flex-1 truncate">
                    {p.questionnaire.name.trim() || (
                      <FormattedMessage
                        id="grow.play.quiz.partLabel"
                        defaultMessage="Part {index}"
                        values={{ index: i + 1 }}
                      />
                    )}
                  </span>
                  <span className={`text-[11px] ${p.answered ? "text-on-ink/70" : "text-ink/45"}`}>
                    {p.answered ? (
                      <FormattedMessage id="grow.play.quiz.partAnswered" defaultMessage="answered" />
                    ) : (
                      <FormattedMessage id="grow.play.quiz.partOpen" defaultMessage="open" />
                    )}
                  </span>
                </button>
              );
            })}
          </div>

          <div className="flex min-h-9 items-center justify-end">
            {next ? (
              <button
                type="button"
                onClick={() =>
                  navigate(
                    `/grow/play/group/${questionnaireGroupId}/${next.questionnaire.questionnaireId}`,
                  )
                }
                className="smycles-button px-5 py-2.5 text-[11px]"
              >
                {answeredCount === 0 ? (
                  <FormattedMessage id="grow.play.quiz.start" defaultMessage="Start quiz" />
                ) : (
                  <FormattedMessage
                    id="grow.play.quiz.continue"
                    defaultMessage="Continue with part {index}"
                    values={{ index: parts.indexOf(next) + 1 }}
                  />
                )}
              </button>
            ) : (
              // No open part left. Membership is the group's, so "all parts
              // answered" is the whole completeness rule - the old fixed count of
              // six existed only because membership was being guessed.
              <button
                type="button"
                onClick={() => navigate(`/grow/play/group/${questionnaireGroupId}/result`)}
                className="smycles-button px-5 py-2.5 text-[11px]"
              >
                <FormattedMessage id="grow.play.quiz.showResult" defaultMessage="Show result" />
              </button>
            )}
          </div>
        </div>
      )}
    </section>
  );
}
